import type { ImageSegmenter } from '@mediapipe/tasks-vision'

/**
 * Desfoque de fundo da camera.
 *
 * Dois caminhos, nessa ordem:
 *
 * 1. **Nativo** — Chrome e Edge expoem a constraint `backgroundBlur`, servida
 *    pelo proprio sistema (no Windows, o Studio Effects). Sai de graca: nada
 *    pra baixar e nenhum frame processado por nos.
 * 2. **MediaPipe** — segmentacao rodando aqui, desenhando num canvas que vira
 *    a stream publicada. E o caminho que o Meet usa, e o unico que funciona em
 *    todo lugar.
 *
 * O processamento acontece no navegador de quem transmite: o servidor nunca ve
 * um frame, aqui como no resto do produto.
 */

const MEDIAPIPE_BASE = '/mediapipe'
const BLUR_RADIUS = 12
/** Bordas ganham um leve esfumado — a mascara do modelo leve e dura demais. */
const MASK_FEATHER = 3

/**
 * Largura em que a segmentacao roda, independente da resolucao da camera.
 *
 * O `selfie_segmenter` processa internamente em 256x256 de qualquer jeito: o
 * que muda ao entregar um quadro maior nao e a qualidade do recorte, e o
 * tamanho da mascara que volta — e ela e paga tres vezes por quadro (leitura
 * da GPU pra CPU, laco em JS, `putImageData`). Manter isso pequeno e a
 * diferenca entre 37 mil e 2 milhoes de posicoes por quadro numa camera 1080p.
 */
const SEGMENT_WIDTH = 256

/** De quanto o quadro encolhe antes de ser desfocado pro fundo. */
const BLUR_DOWNSCALE = 4

/**
 * Cadencia em aba oculta.
 *
 * Nao pode ser zero — parar o canvas congela a imagem pra quem assiste — mas
 * tambem nao precisa ser a cadencia cheia: ninguem esta olhando pra esta aba.
 */
const HIDDEN_FPS = 8

/** De quantos em quantos frames a orientacao da mascara e reavaliada. */
const BACKGROUND_RECHECK_FRAMES = 150
/** Zona morta em volta do 0,5 — dentro dela, nada muda. */
const FLIP_HIGH = 0.65
const FLIP_LOW = 0.35
/** Reavaliacoes seguidas concordando antes de aceitar a inversao. */
const FLIP_VOTES = 2

/**
 * Media da mascara na moldura da imagem.
 *
 * A borda de um enquadramento de webcam e quase toda fundo, entao esse numero
 * diz o que o modelo pontua alto: perto de 1, os valores altos SAO o fundo;
 * perto de 0, sao a pessoa. E assim que descobrimos a convencao em vez de
 * fixa-la no codigo — o `selfie_segmenter` pontua a pessoa, e o palpite
 * contrario desfocava justamente quem devia ficar nitido.
 *
 * O passo acompanha o tamanho da mascara: com ela reduzida a algumas centenas
 * de pixels de largura, um passo fixo de 4 deixaria a moldura com uma dezena
 * de amostras por lado — pouco pra uma media significar alguma coisa.
 */
export function edgeAverage(mask: Float32Array, width: number, height: number): number {
    let total = 0
    let count = 0
    const step = Math.max(1, Math.round(width / 64))
    const add = (value: number | undefined) => {
        if (value === undefined) return
        total += value
        count++
    }

    for (let x = 0; x < width; x += step) {
        add(mask[x])
        add(mask[(height - 1) * width + x])
    }
    for (let y = 0; y < height; y += step) {
        add(mask[y * width])
        add(mask[y * width + width - 1])
    }

    return count === 0 ? 0 : total / count
}

/**
 * O que entra no lugar do fundo.
 *
 * `none` nem monta pipeline — a camera vai crua. `blur` desfoca o proprio
 * quadro; `image` desenha a imagem escolhida (arquivo do computador vira
 * data URL e nunca sai do navegador).
 */
export type BackgroundEffect =
    | { kind: 'none' }
    | { kind: 'blur' }
    | { kind: 'image'; src: string }

export interface BlurPipeline {
    /** O que deve ser publicado no lugar da camera crua. */
    stream: MediaStream
    /** Encerra o processamento. NAO para a camera — quem abriu e que fecha. */
    stop: () => void
}

/** O navegador desfoca sozinho? Chrome/Edge em maquina com suporte. */
export function nativeBlurSupported(): boolean {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices) return false
    const supported = navigator.mediaDevices.getSupportedConstraints() as
        MediaTrackSupportedConstraints & { backgroundBlur?: boolean }
    return supported.backgroundBlur === true
}

/**
 * Tenta ligar o desfoque na propria trilha. `true` quando o sistema assumiu —
 * e ai nao ha pipeline nenhum pra montar.
 */
export async function applyNativeBlur(track: MediaStreamTrack, enabled: boolean): Promise<boolean> {
    if (!nativeBlurSupported()) return false
    try {
        await track.applyConstraints({ backgroundBlur: enabled } as MediaTrackConstraints)
        const settings = track.getSettings() as MediaTrackSettings & { backgroundBlur?: boolean }
        // Nem todo dispositivo honra a constraint: so confia se ela voltou.
        return settings.backgroundBlur === enabled
    } catch {
        return false
    }
}

/**
 * Monta o pipeline de desfoque em cima de uma stream de camera.
 *
 * O import do MediaPipe e dinamico de proposito: sao 11 MB de wasm que so
 * fazem sentido baixar quando alguem liga o efeito. Quem nunca liga nao paga
 * nada por isso existir.
 *
 * ## Onde o custo mora
 *
 * A saida sai na resolucao da camera, mas quase nada aqui e calculado nessa
 * resolucao. Ha duas etapas caras por quadro, e as duas rodam pequenas:
 *
 * - **A segmentacao.** O modelo devolve a mascara no tamanho do que voce
 *   entrega a ele, e cada valor dela atravessa a GPU pra CPU e depois um laco
 *   em JS. Entregando o quadro cheio, uma camera 1080p custava 2,07 milhoes de
 *   posicoes por quadro — 62 milhoes por segundo a 30 fps, na thread principal.
 *   Entregando o quadro reduzido a {@link SEGMENT_WIDTH}, custa ~37 mil.
 * - **O desfoque do fundo.** Desfocar em resolucao cheia e o filtro mais caro
 *   do canvas 2D. Desfocar num quadro reduzido e esticar depois da o mesmo
 *   resultado — a imagem ia ficar borrada de qualquer forma — por uma fracao
 *   do custo.
 */
export async function createBlurPipeline(
    source: MediaStream,
    fps = 30,
    effect: BackgroundEffect = { kind: 'blur' }
): Promise<BlurPipeline> {
    const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision')

    const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_BASE)
    const segmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
            modelAssetPath: `${MEDIAPIPE_BASE}/selfie_segmenter.tflite`,
            delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        // Confianca (0..1) em vez de categoria (0 ou 1): a mascara binaria
        // recorta em degraus, e o contorno fica serrilhado em cima do fundo.
        outputCategoryMask: false,
        outputConfidenceMasks: true
    })

    const track = source.getVideoTracks()[0]
    if (!track) throw new Error('stream sem trilha de video')

    // Carregada antes do primeiro frame: com a imagem chegando no meio, os
    // quadros iniciais sairiam com fundo preto.
    const background = effect.kind === 'image' ? await loadImage(effect.src) : null

    const settings = track.getSettings()
    const width = settings.width ?? 1280
    const height = settings.height ?? 720

    // <video> fora da tela so pra dar frames ao segmentador.
    const video = document.createElement('video')
    video.srcObject = new MediaStream([track])
    video.muted = true
    video.playsInline = true
    await video.play()

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = context2d(canvas)

    // Tamanho em que a segmentacao acontece — a proporcao da camera, reduzida.
    const segWidth = Math.max(16, Math.min(width, SEGMENT_WIDTH))
    const segHeight = Math.max(16, Math.round((segWidth * height) / width))

    // O quadro reduzido que vai PRO MODELO. Reduzir aqui e um desenho de GPU;
    // tudo que vem depois da segmentacao herda o tamanho pequeno.
    const segCanvas = document.createElement('canvas')
    segCanvas.width = segWidth
    segCanvas.height = segHeight
    const segCtx = context2d(segCanvas)

    // Canvas auxiliar: a mascara vem em escala de cinza e vira o recorte da
    // pessoa via composicao, sem precisar percorrer pixel a pixel em JS.
    const maskCanvas = document.createElement('canvas')
    maskCanvas.width = width
    maskCanvas.height = height
    const maskCtx = context2d(maskCanvas)

    // A mascara crua vive num canvas proprio porque `putImageData` ignora
    // filtros: pra suavizar a borda, ela precisa ser REDESENHADA com blur.
    // Fica no tamanho da segmentacao — quem a estica ate o quadro cheio e o
    // `drawImage` la embaixo, que de quebra ja suaviza a borda ao interpolar.
    const rawMaskCanvas = document.createElement('canvas')
    rawMaskCanvas.width = segWidth
    rawMaskCanvas.height = segHeight
    const rawMaskCtx = context2d(rawMaskCanvas)
    const maskImage = rawMaskCtx.createImageData(segWidth, segHeight)

    // Onde o fundo e desfocado, quando o fundo e a propria camera borrada.
    const blurWidth = Math.max(16, Math.round(width / BLUR_DOWNSCALE))
    const blurHeight = Math.max(16, Math.round(height / BLUR_DOWNSCALE))
    const blurCanvas = document.createElement('canvas')
    blurCanvas.width = blurWidth
    blurCanvas.height = blurHeight
    const blurCtx = context2d(blurCanvas)
    // O raio acompanha a reducao: 12 px num quadro quatro vezes menor sairia
    // como 48 px depois de esticado.
    blurCtx.filter = `blur(${(BLUR_RADIUS / BLUR_DOWNSCALE).toFixed(2)}px)`

    let stopped = false
    /** `true` quando os valores altos da mascara sao o fundo. */
    let highIsBackground: boolean | null = null
    let framesSinceCheck = 0
    let flipVotes = 0
    /** Ja houve ao menos um recorte: o canvas da mascara guarda o ultimo. */
    let hasMask = false

    const drawFrame = (timestamp: number) => {
        if (stopped) return
        // Ainda sem quadro decodificado: espera o proximo tique.
        if (video.readyState < 2) return

        try {
            // O modelo recebe o quadro JA reduzido. E aqui que a conta cara
            // deixa de ser cara: a mascara volta neste tamanho.
            segCtx.drawImage(video, 0, 0, segWidth, segHeight)
            const result = segmenter.segmentForVideo(segCanvas, timestamp)
            const mask = result.confidenceMasks?.[0]?.getAsFloat32Array()

            if (mask) {
                if (highIsBackground === null) {
                    highIsBackground = edgeAverage(mask, segWidth, segHeight) > 0.5
                } else if (++framesSinceCheck >= BACKGROUND_RECHECK_FRAMES) {
                    framesSinceCheck = 0
                    // Reavaliar de tempos em tempos protege do caso raro de a
                    // pessoa nem estar no quadro na primeira leitura. Mas a
                    // troca so vale com margem folgada e confirmada duas vezes
                    // seguidas: perto de alguem encostado na borda, a media
                    // fica em cima do 0,5 e a mascara invertia por um frame —
                    // que e exatamente a piscada.
                    const media = edgeAverage(mask, segWidth, segHeight)
                    const sugestao = media > FLIP_HIGH ? true : media < FLIP_LOW ? false : highIsBackground
                    if (sugestao !== highIsBackground) {
                        flipVotes++
                        if (flipVotes >= FLIP_VOTES) {
                            highIsBackground = sugestao
                            flipVotes = 0
                        }
                    } else {
                        flipVotes = 0
                    }
                }

                // Alfa proporcional a confianca: a transicao suave e o que
                // separa um recorte de video-chamada de um recorte de tesoura.
                const pixels = maskImage.data
                for (let i = 0, p = 0; i < mask.length; i++, p += 4) {
                    const person = highIsBackground ? 1 - (mask[i] ?? 0) : (mask[i] ?? 0)
                    pixels[p] = 255
                    pixels[p + 1] = 255
                    pixels[p + 2] = 255
                    pixels[p + 3] = Math.round(person * 255)
                }
                rawMaskCtx.putImageData(maskImage, 0, 0)
                hasMask = true
            }
            result.close()

            // 1. o fundo.
            if (background) {
                drawCover(ctx, background, width, height)
            } else {
                // Desfoca pequeno e estica: o filtro caro roda num quadro
                // BLUR_DOWNSCALE vezes menor em cada eixo, e o proprio upscale
                // contribui pro borrado em vez de atrapalhar.
                blurCtx.drawImage(video, 0, 0, blurWidth, blurHeight)
                ctx.drawImage(blurCanvas, 0, 0, width, height)
            }

            // Frame sem mascara reaproveita a anterior, que continua no
            // canvas: sem isso o quadro sairia so com o fundo, e o sumico da
            // pessoa por um frame e a piscada que da pra ver.
            if (hasMask) {
                // 2. a mascara, esticada ate o quadro cheio e redesenhada com
                // um leve blur — e AQUI que o esfumado entra. Aplicar o filtro
                // ao desenhar o video (como ja esteve) borrava a pessoa
                // inteira, nao a borda dela.
                maskCtx.globalCompositeOperation = 'source-over'
                maskCtx.clearRect(0, 0, width, height)
                maskCtx.filter = `blur(${MASK_FEATHER}px)`
                maskCtx.drawImage(rawMaskCanvas, 0, 0, width, height)
                maskCtx.filter = 'none'

                // 3. o quadro NITIDO recortado por essa mascara...
                maskCtx.globalCompositeOperation = 'source-in'
                maskCtx.drawImage(video, 0, 0, width, height)
                maskCtx.globalCompositeOperation = 'source-over'

                // 4. ...colado por cima do fundo.
                ctx.drawImage(maskCanvas, 0, 0)
            }
        } catch {
            // Um frame perdido nao derruba a transmissao: segue pro proximo.
        }
    }

    /**
     * Relogio do pipeline.
     *
     * Nao da pra usar `requestVideoFrameCallback` nem `setTimeout`: o navegador
     * congela os dois em aba oculta, o canvas para de ser redesenhado e a
     * stream que sai dele trava — quem estava assistindo ve a imagem parada,
     * que foi exatamente o sintoma de trocar de aba com a camera ligada.
     *
     * Um `Worker` tem timer proprio, fora do congelamento da pagina. Ele so
     * avisa a hora de desenhar; o desenho continua aqui, porque o `<video>` e
     * o canvas vivem na thread principal.
     */
    const tickSource = URL.createObjectURL(new Blob([
        `let id = 0
         onmessage = (event) => {
             clearInterval(id)
             if (event.data > 0) id = setInterval(() => postMessage(0), event.data)
         }`
    ], { type: 'text/javascript' }))

    const ticker = new Worker(tickSource)
    ticker.onmessage = () => drawFrame(performance.now())

    /**
     * Cadencia atual.
     *
     * Em aba oculta o pipeline precisa continuar rodando — parar congela a
     * imagem pra quem assiste, que e o motivo de o relogio viver num worker —
     * mas nao precisa continuar rodando a 30 ou 60 fps. Ninguem esta olhando
     * pra ESTA aba; quem assiste ve, no maximo, uma camera menos fluida por
     * alguns segundos. E a diferenca entre um pipeline que esquenta a maquina
     * em segundo plano e um que quase some dela.
     */
    const applyRate = () => {
        const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
        const rate = hidden ? Math.min(HIDDEN_FPS, fps) : fps
        ticker.postMessage(Math.round(1000 / rate))
    }
    applyRate()
    document.addEventListener('visibilitychange', applyRate)

    const output = canvas.captureStream(fps)

    return {
        stream: output,
        stop: () => {
            stopped = true
            document.removeEventListener('visibilitychange', applyRate)
            ticker.postMessage(0)
            ticker.terminate()
            URL.revokeObjectURL(tickSource)
            // So as trilhas do canvas: a camera continua viva porque pode ser
            // reaproveitada ao desligar o efeito (e quem a abriu e que a fecha).
            for (const outputTrack of output.getTracks()) outputTrack.stop()
            video.srcObject = null
            closeSegmenter(segmenter)
        }
    }
}

function context2d(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas 2d indisponivel')
    return ctx
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const image = new Image()
        // Precisa disto pra imagem de outra origem nao "sujar" o canvas: um
        // canvas sujo nao deixa `captureStream` publicar nada.
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error(`nao carregou o fundo: ${src}`))
        image.src = src
    })
}

/**
 * Desenha cobrindo o quadro inteiro, cortando o excedente — igual ao
 * `object-fit: cover`. Esticar deformaria um fundo de proporcao diferente.
 */
function drawCover(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    width: number,
    height: number
) {
    const scale = Math.max(width / image.width, height / image.height)
    const drawWidth = image.width * scale
    const drawHeight = image.height * scale
    ctx.drawImage(image, (width - drawWidth) / 2, (height - drawHeight) / 2, drawWidth, drawHeight)
}

function closeSegmenter(segmenter: ImageSegmenter) {
    try {
        segmenter.close()
    } catch {
        // Ja fechado — nada a fazer.
    }
}
